import { useEffect, type MutableRefObject } from "react";
import { Controller, FormProvider, useFieldArray, useForm, type Control } from "react-hook-form";
import Button from "@/components/Button";
import DateInput from "@/components/DateInput";
import Icon from "@/components/Icon";
import ListItem from "@/components/ListItem";
import SectionHeading from "@/components/SectionHeading";
import Select from "@/components/Select";
import TextField from "@/components/TextField";
import BeneficiaryFormCard from "./BeneficiaryFormCard";
import BeneficiaryContactFormFields from "./BeneficiaryContactFormFields";
import {
  allocationSummaryHeadline,
  beneficiariesFormCanSave,
  blankBeneficiariesFormValues,
  emptyBeneficiary,
  RELATIONSHIP_OPTIONS,
  requiredText,
  ssnLast4Validation,
  sumPercents,
  validateEffectiveStartDate,
  type BeneficiariesFormValues,
  type BeneficiaryFormRecord,
  type BeneficiaryGroup
} from "./beneficiaryFormUtils";

export type UpdateBeneficiariesFormActions = {
  submit: () => void;
  clearData: () => void;
};

export type UpdateBeneficiariesFormProps = {
  defaultValues: BeneficiariesFormValues;
  applicationType: string;
  onSubmit: (values: BeneficiariesFormValues) => void;
  formActionsRef?: MutableRefObject<UpdateBeneficiariesFormActions | null>;
  disabled?: boolean;
  contactInfoEnabled?: boolean;
  onFormStateChange?: (state: { canSave: boolean; isDirty: boolean }) => void;
};

function useBeneficiaryFormState(defaultValues: BeneficiariesFormValues, contactInfoEnabled: boolean) {
  const form = useForm<BeneficiariesFormValues>({
    defaultValues,
    mode: "onChange"
  });

  const watchedPrimary = form.watch("primaryBeneficiaries");
  const watchedContingent = form.watch("contingentBeneficiaries");
  const watchedEffectiveStartDate = form.watch("effectiveStartDate");
  const primaryPercentSum = sumPercents(watchedPrimary);
  const contingentPercentSum = sumPercents(watchedContingent);
  const primaryPercentValid = watchedPrimary.length > 0 && primaryPercentSum === 100;
  const contingentPercentValid = watchedContingent.length === 0 || contingentPercentSum === 100;
  const canSave = beneficiariesFormCanSave(
    {
      effectiveStartDate: watchedEffectiveStartDate,
      primaryBeneficiaries: watchedPrimary,
      contingentBeneficiaries: watchedContingent
    },
    contactInfoEnabled
  );

  return {
    ...form,
    primaryPercentSum,
    contingentPercentSum,
    primaryPercentValid,
    contingentPercentValid,
    canSave
  };
}

export default function UpdateBeneficiariesForm({
  defaultValues,
  applicationType,
  onSubmit,
  formActionsRef,
  disabled = false,
  contactInfoEnabled = false,
  onFormStateChange
}: UpdateBeneficiariesFormProps) {
  const form = useBeneficiaryFormState(defaultValues, contactInfoEnabled);
  const {
    control,
    handleSubmit,
    reset,
    canSave,
    primaryPercentSum,
    contingentPercentSum,
    primaryPercentValid,
    contingentPercentValid,
    formState: { isDirty }
  } = form;

  useEffect(() => {
    onFormStateChange?.({ canSave, isDirty });
  }, [canSave, isDirty, onFormStateChange]);

  useEffect(() => {
    if (!formActionsRef) return;

    formActionsRef.current = {
      submit: () => {
        void handleSubmit((values) => {
          if (!beneficiariesFormCanSave(values, contactInfoEnabled)) return;
          onSubmit(values);
        })();
      },
      clearData: () => {
        reset(blankBeneficiariesFormValues(applicationType), { keepDefaultValues: true });
      }
    };
  }, [applicationType, contactInfoEnabled, formActionsRef, handleSubmit, onSubmit, reset]);

  const {
    fields: primaryFields,
    append: appendPrimary,
    remove: removePrimary
  } = useFieldArray({
    control,
    name: "primaryBeneficiaries",
    keyName: "fieldId"
  });
  const {
    fields: contingentFields,
    append: appendContingent,
    remove: removeContingent
  } = useFieldArray({
    control,
    name: "contingentBeneficiaries",
    keyName: "fieldId"
  });

  return (
    <FormProvider {...form}>
      <div className="flex flex-col" data-testid="update-beneficiaries-form">
        <div className="flex flex-col">
          <SectionHeading
            heading="Effective Start Date"
            body="Date this beneficiary designation takes effect. You can choose today, an earlier date, or a future date."
          />
          <Controller
            name="effectiveStartDate"
            control={control}
            rules={{
              required: "Effective start date is required",
              validate: validateEffectiveStartDate
            }}
            render={({ field, fieldState }) => (
              <DateInput
                label="Effective Start Date *"
                placeholder="MM/DD/YYYY"
                supportingText="MM/DD/YYYY"
                value={field.value || null}
                onChange={(next) => field.onChange(next ?? "")}
                onBlur={field.onBlur}
                error={!!fieldState.error}
                errorMessage={fieldState.error?.message}
                disabled={disabled}
                required
              />
            )}
          />
        </div>

        <div className="flex flex-col">
          <BeneficiaryFieldsSection
            heading="Primary Beneficiaries"
            description="At least one primary beneficiary is required."
            fields={primaryFields}
            group="primaryBeneficiaries"
            control={control}
            canRemove={() => primaryFields.length > 1}
            onRemove={removePrimary}
            allocationSum={primaryPercentSum}
            allocationValid={primaryPercentValid}
            disabled={disabled}
            contactInfoEnabled={contactInfoEnabled}
          />

          <div className="self-start">
            <Button
              type="button"
              variant="filledTonal"
              icon="add"
              onClick={() => appendPrimary(emptyBeneficiary(applicationType))}
              disabled={disabled}
              testId="add-primary-beneficiary"
            >
              Add Primary Beneficiary
            </Button>
          </div>
        </div>

        <div className="flex flex-col">
          <BeneficiaryFieldsSection
            heading="Contingent Beneficiaries"
            description="Contingent beneficiaries are optional."
            fields={contingentFields}
            group="contingentBeneficiaries"
            control={control}
            canRemove={() => true}
            onRemove={removeContingent}
            allocationSum={contingentPercentSum}
            allocationValid={contingentPercentValid}
            disabled={disabled}
            contactInfoEnabled={contactInfoEnabled}
          />

          <div className="self-start">
            <Button
              type="button"
              variant="filledTonal"
              icon="add"
              onClick={() => appendContingent(emptyBeneficiary(applicationType))}
              disabled={disabled}
              testId="add-contingent-beneficiary"
            >
              Add Contingent Beneficiary
            </Button>
          </div>
        </div>
      </div>
    </FormProvider>
  );
}

type BeneficiaryFieldsSectionProps = {
  heading: string;
  description: string;
  fields: Array<BeneficiaryFormRecord & { fieldId: string }>;
  group: BeneficiaryGroup;
  control: Control<BeneficiariesFormValues>;
  canRemove: (index: number) => boolean;
  onRemove: (index: number) => void;
  allocationSum: number;
  allocationValid: boolean;
  disabled: boolean;
  contactInfoEnabled: boolean;
};

function BeneficiaryFieldsSection({
  heading,
  description,
  fields,
  group,
  control,
  canRemove,
  onRemove,
  allocationSum,
  allocationValid,
  disabled,
  contactInfoEnabled
}: BeneficiaryFieldsSectionProps) {
  const groupLabel = group === "primaryBeneficiaries" ? "Primary" : "Contingent";

  return (
    <div className="flex flex-col" data-testid={`${group}-section`}>
      <SectionHeading heading={heading} body={description} />

      <div className="flex flex-col gap-6">
        {fields.map((field, index) => (
          <BeneficiaryFormCard
            key={field.fieldId}
            heading={`${groupLabel} Beneficiary ${index + 1}`}
            removeAriaLabel={`Remove ${groupLabel} Beneficiary ${index + 1}`}
            headerEnd={<BeneficiaryPercentField control={control} group={group} index={index} disabled={disabled} />}
            onRemove={canRemove(index) ? () => onRemove(index) : undefined}
            data-testid={`${group}-card-${index}`}
          >
            <BeneficiaryRowFields
              control={control}
              group={group}
              index={index}
              disabled={disabled}
              contactInfoEnabled={contactInfoEnabled}
            />
          </BeneficiaryFormCard>
        ))}
      </div>

      {fields.length > 0 && (
        <AllocationSummary group={group} allocationSum={allocationSum} allocationValid={allocationValid} />
      )}
    </div>
  );
}

type AllocationSummaryProps = {
  group: BeneficiaryGroup;
  allocationSum: number;
  allocationValid: boolean;
};

function AllocationSummary({ group, allocationSum, allocationValid }: AllocationSummaryProps) {
  return (
    <div className="mb-6 mt-4" data-testid={`${group}-allocation-summary`}>
      <ListItem
        compact
        leading={
          <span aria-hidden="true" className="flex h-6 w-6 shrink-0 items-center justify-center">
            {allocationValid ? (
              <Icon name="check_circle" size={24} filled className="text-extended-success-brand-color" />
            ) : (
              <Icon name="cancel" size={24} filled className="text-sys-brand-error" />
            )}
          </span>
        }
        headline={allocationSummaryHeadline(allocationSum, allocationValid)}
      />
    </div>
  );
}

type BeneficiaryFieldProps = {
  control: Control<BeneficiariesFormValues>;
  group: BeneficiaryGroup;
  index: number;
  disabled: boolean;
};

type BeneficiaryRowFieldProps = BeneficiaryFieldProps & {
  contactInfoEnabled: boolean;
};

function BeneficiaryPercentField({ control, group, index, disabled }: BeneficiaryFieldProps) {
  return (
    <Controller
      name={`${group}.${index}.percent` as const}
      control={control}
      rules={{
        required: "Percentage is required",
        validate: (value) => {
          const percent = Number(value);
          return (percent >= 1 && percent <= 100) || "Must be between 1 and 100";
        }
      }}
      render={({ field, fieldState }) => (
        <TextField
          label="Benefit Amount (%)"
          className="bg-sys-brand-surface lg:max-w-[240px]"
          value={field.value ?? ""}
          onChangeText={(value) => field.onChange(value.replace(/\D/g, "").slice(0, 3))}
          onBlur={field.onBlur}
          error={!!fieldState.error}
          errorText={fieldState.error?.message}
          type="number"
          trailingIcon="percent"
          required
          disabled={disabled}
          data-testid={`${group}-percent-${index}`}
        />
      )}
    />
  );
}

function BeneficiaryRowFields({ control, group, index, disabled, contactInfoEnabled }: BeneficiaryRowFieldProps) {
  return (
    <>
      <Controller
        name={`${group}.${index}.fullName` as const}
        control={control}
        rules={{
          required: "Full name is required",
          validate: (value) => requiredText(value) || "Full name is required"
        }}
        render={({ field, fieldState }) => (
          <TextField
            label="Full Name"
            className="bg-sys-brand-surface"
            value={field.value ?? ""}
            onChangeText={field.onChange}
            onBlur={field.onBlur}
            error={!!fieldState.error}
            errorText={fieldState.error?.message}
            required
            disabled={disabled}
          />
        )}
      />
      <Controller
        name={`${group}.${index}.relationship` as const}
        control={control}
        rules={{
          required: "Relationship is required",
          validate: (value) => requiredText(value) || "Relationship is required"
        }}
        render={({ field, fieldState }) => (
          <Select
            label="Relationship"
            options={RELATIONSHIP_OPTIONS}
            value={field.value ?? ""}
            onSelect={field.onChange}
            error={!!fieldState.error}
            errorText={fieldState.error?.message}
            required
            disabled={disabled}
          />
        )}
      />
      <Controller
        name={`${group}.${index}.dateOfBirth` as const}
        control={control}
        rules={{
          required: "Date of birth is required",
          validate: (value) => requiredText(value) || "Date of birth is required"
        }}
        render={({ field, fieldState }) => (
          <DateInput
            label="Date of Birth *"
            value={field.value || null}
            onChange={(next) => field.onChange(next ?? "")}
            onBlur={field.onBlur}
            useModal
            maxDate={new Date()}
            error={!!fieldState.error}
            errorMessage={fieldState.error?.message}
            disabled={disabled}
            required
          />
        )}
      />
      <Controller
        name={`${group}.${index}.ssnLast4` as const}
        control={control}
        rules={{
          required: "Last 4 of SSN is required",
          validate: (value, values) => ssnLast4Validation(value, values[group]?.[index])
        }}
        render={({ field, fieldState }) => (
          <TextField
            label="Last 4 of SSN"
            className="bg-sys-brand-surface"
            value={field.value ?? ""}
            onChangeText={field.onChange}
            onBlur={field.onBlur}
            error={!!fieldState.error}
            errorText={fieldState.error?.message}
            type="password"
            required
            disabled={disabled}
          />
        )}
      />
      {contactInfoEnabled ? (
        <BeneficiaryContactFormFields control={control} group={group} index={index} disabled={disabled} />
      ) : null}
    </>
  );
}
